import type { AuditTimelineChange, AuditTimelineEntry } from '../../types/auditTimeline';
import { labelForField } from './fieldLabels';

// Pure: take an audit entry, return a human-readable description split
// into a one-line summary + optional bullet list of per-field changes.
// The timeline component renders the returned shape verbatim, so this
// function encodes every formatting decision (context special-casing,
// array-change summaries, scalar diff rendering) in one place.
//
// Design goals:
//   - Context-scoped entries (opt-out, reverse, refresh, duplicate) render
//     their domain verb and SKIP the field-diff list. The raw changes are
//     still in the audit doc for anyone inspecting server-side.
//   - Generic entries show "{actor} changed [label] from [old] to [new]"
//     for a single scalar, or a bulleted list otherwise.
//   - Array / nested-object changes render as short summaries ("updated
//     Meta ad copy", "reordered Meta headlines"), not deep diffs.
//
// Kept pure so it gets a focused unit test without React.

export type AuditDescription = {
  summary: string;       // e.g. "Scott M. changed Meta Monthly Budget from $500 to $600"
  bullets: string[];     // optional per-field descriptions for multi-field updates
};

function isArray(v: unknown): v is unknown[] {
  return Array.isArray(v);
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === 'object' && !Array.isArray(v);
}

function shortActor(email: string): string {
  // formatUserDisplayName is for User objects; here we only have the
  // audit entry's actor (the user's email at write time). Use the
  // local-part so the timeline reads naturally in English sentences.
  const at = email.indexOf('@');
  return at > 0 ? email.slice(0, at) : email;
}

function renderScalar(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value.length > 0 ? value : '""';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // Anything else (objects, dates, unknown shapes) falls back to a
  // compact JSON rendering so the timeline never shows "[object Object]".
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function describeArrayChange(
  label: string,
  before: unknown[],
  after: unknown[],
): string {
  // Sets only — order equality uses JSON.stringify elsewhere, but for
  // "was it added or removed" semantics we compare membership. Works for
  // arrays of primitives (headlines, descriptions); falls back to a
  // stringified-set comparison for arrays of objects (metaAssetLabels).
  const toKey = (v: unknown) => (isPlainObject(v) || isArray(v) ? JSON.stringify(v) : String(v));
  const beforeKeys = new Set(before.map(toKey));
  const afterKeys = new Set(after.map(toKey));
  let added = 0;
  let removed = 0;
  for (const k of afterKeys) if (!beforeKeys.has(k)) added++;
  for (const k of beforeKeys) if (!afterKeys.has(k)) removed++;
  if (added === 0 && removed === 0) {
    // Contents unchanged but JSON.stringify(before) !== JSON.stringify(after)
    // means order changed.
    return `reordered ${label}`;
  }
  const parts: string[] = [];
  if (added > 0) parts.push(`+${added}`);
  if (removed > 0) parts.push(`−${removed}`);
  return `updated ${label} (${parts.join(', ')})`;
}

// Multi Lead Form spec §1.4: when the row's `leadForm` snapshot changes
// the audit doc records both `leadFormId` and `leadForm` together (the
// server stamps them atomically in patchRowInline / runBulkAction). The
// "Lead form snapshot" bullet from the generic path doesn't surface the
// new name — render a friendly summary instead.
function describeLeadFormChange(change: AuditTimelineChange): string {
  const after = change.after;
  const before = change.before;
  if (isPlainObject(after) && typeof after.name === 'string') {
    const newName = after.name;
    if (isPlainObject(before) && typeof before.name === 'string' && before.name !== newName) {
      return `Row reassigned to Lead Form: '${newName}' (was '${before.name}')`;
    }
    return `Row reassigned to Lead Form: '${newName}'`;
  }
  if (after === null) return 'Lead Form cleared from this row';
  return 'updated Lead form snapshot';
}

function describeChange(change: AuditTimelineChange): string {
  const { field, before, after } = change;
  const label = labelForField(field);

  // Multi Lead Form spec §1.4: dedicated render for the row's lead form
  // snapshot. `leadForm` carries the full CampaignLeadForm; `leadFormId`
  // is the pointer. We surface a single friendly summary off `leadForm`
  // and silently swallow the paired `leadFormId` change to avoid double
  // bullets — handled in the multi-change branch below.
  if (field === 'leadForm') {
    return describeLeadFormChange(change);
  }

  // Pinned Headlines spec §2.5: when the changed field is googleAssets
  // and only the headlines sub-array differs, produce a pin/text-aware
  // summary instead of the generic "updated Google Assets". If
  // descriptions / paths also changed alongside headlines we fall back
  // to the generic — the heuristic only fires on clean headline edits.
  if (
    field === 'googleAssets' &&
    isPlainObject(before) &&
    isPlainObject(after)
  ) {
    const headlineMsg = describeGoogleHeadlinesSubChange(before, after);
    if (headlineMsg) return headlineMsg;
  }

  if (isArray(before) || isArray(after)) {
    return describeArrayChange(
      label,
      isArray(before) ? before : [],
      isArray(after) ? after : [],
    );
  }
  if (isPlainObject(before) || isPlainObject(after)) {
    // Nested objects (metaAdCopy, googleAssets, utmConfig, landing
    // overrides) — summarise, don't diff. The audit doc retains full
    // before/after for anyone who needs to dig deeper.
    return `updated ${label}`;
  }
  return `changed ${label} from ${renderScalar(before)} to ${renderScalar(after)}`;
}

// Pinned Headlines spec §2.5 helper. Inspect the headlines sub-array
// of a googleAssets diff; return a friendly summary when the diff is
// purely headlines, or null when other googleAssets sub-fields also
// changed (caller falls back to generic "updated Google Assets").
//
// Pure: no React or Firestore types, just object-walking. The
// before/after objects come from the audit log so they're plain JSON.
type AuditedHeadline = { text: string; pinPosition: 1 | 2 | 3 | null };

type HeadlineDelta =
  | { kind: 'added'; text: string; pin: 1 | 2 | 3 | null }
  | { kind: 'removed'; text: string }
  | { kind: 'pinned'; text: string; pin: 1 | 2 | 3 }
  | { kind: 'unpinned'; text: string }
  | { kind: 'pin_moved'; text: string; from: 1 | 2 | 3; to: 1 | 2 | 3 };

function isAuditedHeadlineArray(v: unknown): v is AuditedHeadline[] {
  if (!Array.isArray(v)) return false;
  return v.every(
    (h) =>
      isPlainObject(h) &&
      typeof (h as Record<string, unknown>).text === 'string' &&
      ('pinPosition' in (h as Record<string, unknown>)),
  );
}

function quote(text: string): string {
  // Truncate aggressively so a single long headline doesn't overrun
  // the timeline row. 60 chars is enough to identify the headline
  // without word-wrapping the bullet.
  const trimmed = text.length > 60 ? `${text.slice(0, 57)}...` : text;
  return `'${trimmed}'`;
}

function describeGoogleHeadlinesSubChange(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
): string | null {
  // Bail unless headlines is the only sub-field that differs. Same
  // JSON.stringify equality the audit pipeline uses everywhere — keeps
  // the precision stable across nested objects.
  const beforeKeys = Object.keys(before);
  const afterKeys = Object.keys(after);
  const allKeys = new Set([...beforeKeys, ...afterKeys]);
  let onlyHeadlines = true;
  for (const k of allKeys) {
    if (k === 'headlines') continue;
    if (JSON.stringify(before[k]) !== JSON.stringify(after[k])) {
      onlyHeadlines = false;
      break;
    }
  }
  if (!onlyHeadlines) return null;

  const beforeHeadlines = before.headlines;
  const afterHeadlines = after.headlines;
  if (!isAuditedHeadlineArray(beforeHeadlines) || !isAuditedHeadlineArray(afterHeadlines)) {
    return null;
  }

  // Index by text on each side so we can spot pin-only changes (same
  // text, different pinPosition) cheaply. If multiple headlines share
  // a text, the first occurrence wins — that matches what an AM sees
  // in the editor (Google's RSA itself doesn't disambiguate duplicates).
  const beforeByText = new Map<string, AuditedHeadline>();
  for (const h of beforeHeadlines) {
    if (!beforeByText.has(h.text)) beforeByText.set(h.text, h);
  }
  const afterByText = new Map<string, AuditedHeadline>();
  for (const h of afterHeadlines) {
    if (!afterByText.has(h.text)) afterByText.set(h.text, h);
  }

  const deltas: HeadlineDelta[] = [];
  for (const h of afterHeadlines) {
    const prev = beforeByText.get(h.text);
    if (!prev) {
      deltas.push({ kind: 'added', text: h.text, pin: h.pinPosition });
      continue;
    }
    if (prev.pinPosition === h.pinPosition) continue;
    if (prev.pinPosition === null && h.pinPosition !== null) {
      deltas.push({ kind: 'pinned', text: h.text, pin: h.pinPosition });
    } else if (prev.pinPosition !== null && h.pinPosition === null) {
      deltas.push({ kind: 'unpinned', text: h.text });
    } else if (prev.pinPosition !== null && h.pinPosition !== null) {
      deltas.push({
        kind: 'pin_moved',
        text: h.text,
        from: prev.pinPosition,
        to: h.pinPosition,
      });
    }
  }
  for (const h of beforeHeadlines) {
    if (!afterByText.has(h.text)) {
      deltas.push({ kind: 'removed', text: h.text });
    }
  }

  if (deltas.length === 0) {
    // Order-only change (same texts + pins but reordered) — fall back
    // to the generic array-reorder summary.
    return `reordered Google Headlines`;
  }
  if (deltas.length === 1) {
    return describeHeadlineDelta(deltas[0]!);
  }
  // Multi-change update — single summary line per spec.
  return `made ${deltas.length} Google headline changes (pin/text)`;
}

function describeHeadlineDelta(d: HeadlineDelta): string {
  switch (d.kind) {
    case 'added':
      return d.pin !== null
        ? `added Google headline ${quote(d.text)} pinned to position ${d.pin}`
        : `added Google headline ${quote(d.text)}`;
    case 'removed':
      return `removed Google headline ${quote(d.text)}`;
    case 'pinned':
      return `pinned Google headline ${quote(d.text)} to position ${d.pin}`;
    case 'unpinned':
      return `unpinned Google headline ${quote(d.text)}`;
    case 'pin_moved':
      return `moved Google headline ${quote(d.text)} from position ${d.from} to position ${d.to}`;
  }
}

export function describeAuditEntry(entry: AuditTimelineEntry): AuditDescription {
  const actor = shortActor(entry.actor);

  // Context-scoped special cases (P2b-2 §7.5.3). Context markers are
  // already being written by Chunks A and B — we just read them here.
  if (entry.context === 'mark_row_as_opt_out') {
    return { summary: `${actor} marked row as Opted Out`, bullets: [] };
  }
  if (entry.context === 'reverse_row_opt_out') {
    return { summary: `${actor} reversed Opt Out`, bullets: [] };
  }
  if (entry.context === 'refresh_from_campaign') {
    const n = entry.changes.length;
    return {
      summary: `${actor} refreshed content from Campaign (${n} field${n === 1 ? '' : 's'} updated)`,
      bullets: [],
    };
  }
  // Granular Refresh spec §4.3. Bulk partial refresh — surface WHICH
  // fields were refreshed (friendly labels), not just a count. Bookkeeping
  // fields the server always rewrites are noise; the leadFormId pointer is
  // collapsed into the leadForm snapshot label (they're written as a pair).
  if (entry.context === 'refresh_fields') {
    const BOOKKEEPING = new Set([
      'contentLastEditedAt',
      'contentLastEditedBy',
      'updatedAt',
    ]);
    let fields = entry.changes
      .map((c) => c.field)
      .filter((f) => !BOOKKEEPING.has(f));
    if (fields.includes('leadForm')) {
      fields = fields.filter((f) => f !== 'leadFormId');
    }
    const labels = [...new Set(fields.map((f) => labelForField(f)))];
    if (labels.length === 0) {
      return {
        summary: `${actor} refreshed from Campaign (no field changes)`,
        bullets: [],
      };
    }
    const shown = labels.slice(0, 5);
    const extra = labels.length - shown.length;
    const list =
      extra > 0 ? `${shown.join(', ')} +${extra} more` : shown.join(', ');
    return {
      summary: `${actor} refreshed from Campaign: ${list}`,
      bullets: [],
    };
  }
  if (entry.context === 'duplicate_row') {
    return { summary: `${actor} created a duplicate row`, bullets: [] };
  }

  // Operation-scoped defaults.
  if (entry.operation === 'create') {
    return { summary: `${actor} created this row`, bullets: [] };
  }
  if (entry.operation === 'delete') {
    return { summary: `${actor} deleted this row`, bullets: [] };
  }

  // Updates fall through to field-diff rendering.
  if (entry.changes.length === 0) {
    // Shouldn't happen (buildAuditEntry skips no-op updates), but defend
    // against legacy data so the timeline doesn't render a blank row.
    return { summary: `${actor} made an update`, bullets: [] };
  }
  // Multi Lead Form spec §1.4: when the row's leadForm + leadFormId
  // change together (server stamps them atomically), suppress the
  // pointer bullet so the timeline doesn't show "Row reassigned to Lead
  // Form" alongside an unfriendly "changed Lead form selection from
  // lf-a to lf-b" bullet. Same rule for the inline-patch path and the
  // bulk path — both write the pair.
  const hasLeadFormChange = entry.changes.some((c) => c.field === 'leadForm');
  const effectiveChanges = hasLeadFormChange
    ? entry.changes.filter((c) => c.field !== 'leadFormId')
    : entry.changes;
  if (effectiveChanges.length === 1) {
    return {
      summary: `${actor} ${describeChange(effectiveChanges[0]!)}`,
      bullets: [],
    };
  }
  return {
    summary: `${actor} made ${effectiveChanges.length} changes`,
    bullets: effectiveChanges.map((c) => describeChange(c)),
  };
}
